import { Button, ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

interface CopyToClipboardProps {
  colorVariant?: ButtonColorVariant;
  tooltip?: string;
  value: string | (() => string);
}

export function CopyToClipboard({ colorVariant, tooltip, value }: CopyToClipboardProps) {
  const { t } = useTranslation("common");
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(typeof value === "function" ? value() : value);
      setCopied(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 2000);
    } catch {
      toast.error(t("ErrorCopyingToClipboard"));
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Button
      type="button"
      variant={ButtonVariant.Ghost}
      colorVariant={colorVariant}
      size={ButtonSize.Sm}
      onClick={handleCopy}
      className="h-6 w-6 shrink-0 p-0"
      tooltip={tooltip || t("Copy")}
    >
      {copied ? <Check className="text-success h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}
